import { BookingType, Item } from '@/tracking/analytics/types';
import {
    ActivityProduct,
    AnyBookingAddonProduct,
    AnyBookingProduct,
    BaggageInsuranceProduct,
    CancellationInsuranceProduct,
    CustomBaggageProduct,
    // FerryAccommodationType,
    FerryLeg,
    FerryProduct,
    FerrySegment,
    FlightLeg,
    FlightPax,
    FlightProduct,
    FlightSegment,
    GolfProduct,
    GolfTicket,
    HotelProduct,
    ProductType,
    ServicePackageAccommodationProduct,
    ServicePackageProduct,
    TransferProduct,
} from '@/api/MyBooking/myBooking.interfaces';
import {
    MyBookingFlightBaggage,
    MyBookingFlightSeat,
    // MyBookingFerryAccommodation,
    //  MyBookingFerryAddon,
    // MyBookingFerryLeg,
    MyBookingFerryCustomFields,
} from '@/tracking/analytics/MyBooking/types';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';
import groupBy from 'lodash/groupBy';

export const mapBookingProductsAsItems = (bookingProducts: AnyBookingProduct[]): Item[] => {
    const products: Item[] = mapProductsAsItems([...bookingProducts]);

    return [...products];
};

const mapProductsAsItems = (bookingProducts: AnyBookingProduct[]): Item[] => {
    const items: Item[] = [];
    [...bookingProducts].forEach((p) => {
        switch (p.type) {
            case ProductType.Hotel:
                items.push(mapHotelProductItem(p as HotelProduct));
                break;
            case ProductType.Flight:
                items.push(mapFlightProductItem(p as FlightProduct));
                break;
            case ProductType.Ferry:
                items.push(mapFerryProductItem(p as FerryProduct));
                break;
            case ProductType.Activity:
                items.push(mapActivityProductItem(p as ActivityProduct));
                break;
            case ProductType.Golf:
                items.push(mapGolfProductItem(p as GolfProduct));
                break;
            case ProductType.Transfer:
                items.push(mapTransferProductItem(p as TransferProduct));
                break;
            case ProductType.CancellationInsurance:
                items.push(mapAddonProductItem(p as CancellationInsuranceProduct));
                break;
            case ProductType.ServicePackageBasic:
            case ProductType.ServicePackagePremium:
                items.push(mapAddonProductItem(p as ServicePackageProduct));
                break;
            case ProductType.ServicePackageAccommodation:
                items.push(mapAddonProductItem(p as ServicePackageAccommodationProduct));
                break;
            case ProductType.BaggageInsurance:
                items.push(mapAddonProductItem(p as BaggageInsuranceProduct));
                break;
            case ProductType.CustomBaggage:
                items.push(mapCustomBaggageProductItem(p as CustomBaggageProduct));
                break;
        }
    });

    return items;
};

const mapHotelProductItem = (product: HotelProduct, quantity = 1): Item => {
    const address = product.address
        ? `(${product.address?.city} - ${product.address?.country})`
        : '';

    return {
        quantity,
        item_id: product.hotelCode,
        item_name: `${product.hotelName}`,
        item_category: BookingType.Accommodation,
        item_category2: product.type,
        my_booking_hotel: {
            destination: `${product.hotelName}${address}`,
            hotel_city: product.address?.city ?? '',
            hotel_country: product.address?.country ?? '',
            start_date: product.fromDate,
            end_date: product.toDate,
            rooms: product.rooms.map((room) => ({
                productId: room.productId,
                description: room.roomDescription,
                adults: room.adults,
                children: room.children,
            })),
        },
    };
};

const mapFlightProductItem = (product: FlightProduct, quantity = 1): Item => {
    const mapFlightLeg = (leg: FlightLeg) => {
        const { departureAirportCode, departureDate } = leg.segments[0] as FlightSegment;
        const { arrivalDate, arrivalAirportCode } = leg.segments[
            leg.segments.length - 1
        ] as FlightSegment;
        return {
            origin: departureAirportCode,
            startDate: departureDate,
            endDate: arrivalDate,
            destination: arrivalAirportCode,
        };
    };

    const mapFlightSeats = (pax: FlightPax[]) => {
        return pax
            .filter((s) => s !== null)
            .flatMap((p) => p.seats)
            .map((s) => ({
                arrivalAirportCode: s && s.arrivalAirportCode,
                departureAirportCode: s && s.departureAirportCode,
                row: s && s.row,
                column: s && s.column,
            })) as MyBookingFlightSeat[];
    };

    const mapFlightBaggages = (pax: FlightPax[]) => {
        const baggages: MyBookingFlightBaggage[] = [];

        const flattenBaggages = pax
            .filter((s) => s !== null)
            .flatMap((p) => p.baggages)
            .map((b) => ({
                type: b && b.type,
                pieces: b && b.pieces,
            }));

        const bagaggesByType = groupBy(flattenBaggages, 'type');

        for (const key in bagaggesByType) {
            baggages.push({
                type: key,
                pieces: sumBy(bagaggesByType[key], 'pieces'),
            });
        }

        return baggages;
    };

    const airlineNumber = product.ticket.airlineNumber || '';
    const [outbound, inbound] = product.legs;
    const seats = mapFlightSeats(product.pax);
    const baggages = mapFlightBaggages(product.pax);

    return {
        quantity,
        item_id: airlineNumber,
        item_name: `${product.type}`,
        item_variant: product.ticket.type,
        item_category: 'Transport',
        item_category2: product.type,
        my_booking_flight: {
            passengers: product.pax.length,
            outbound: mapFlightLeg(outbound),
            ...(inbound && { inbound: mapFlightLeg(inbound) }),
            ...(seats.length && { seats }),
            ...(baggages.length && { baggages }),
        },
    };
};

const mapFerryProductItem = (product: FerryProduct, quantity = 1): Item => {
    const mapFerryLeg = (leg: FerryLeg) => {
        const { departureDate, ferryName, legName } = leg.segments[0] as FerrySegment;

        const { arrivalDate } = leg.segments[leg.segments.length - 1] as FerrySegment;

        return {
            startDate: departureDate,
            endDate: arrivalDate,
            legName: legName ?? '',
            ferryName: ferryName ?? '',
        };
    };

    const [outbound, inbound] = product.legs;

    function getFerryData(): MyBookingFerryCustomFields {
        return {
            passengers: product.pax.length,
            outbound: mapFerryLeg(outbound),
            ...(inbound && { inbound: mapFerryLeg(inbound) }),
            // ...(vehicles.length && { vehicles: vehicles }),
            // ...(accommodations.length && { accommodations: accommodations }),
            // ...(addons.length && { addons: addons }),
        };
    }

    return {
        quantity,
        item_id: product.productId.toString(),
        item_name: `${product.type}`,
        item_category: 'Transport',
        item_category2: product.type,
        my_booking_ferry: getFerryData(),
    };
};

const mapActivityProductItem = (product: ActivityProduct, quantity = 1): Item => {
    return {
        quantity,
        item_id: product.placeCode,
        item_name: `${product.type}`,
        item_variant: `${product.placeName}`,
        item_category: 'Activities',
        item_category2: product.type,
        my_booking_activity: {
            placeCode: product.placeCode,
            placeName: product.placeName,
            date: product.date,
        },
    };
};

const mapGolfProductItem = (product: GolfProduct, quantity = 1): Item => {
    const adults = sum(product.tickets.flatMap((ticket) => ticket.adults));
    const children = sum(product.tickets.flatMap((ticket) => ticket.children));

    const mapTickets = (golfTickets: GolfTicket[]) =>
        golfTickets.map((ticket) => ({
            productId: ticket.productId,
            ticketDescription: ticket.ticketDescription,
        }));

    const address = product.address
        ? `(${product.address?.city} - ${product.address?.country})`
        : '';

    return {
        quantity,
        item_id: product.golfCode,
        item_name: `${product.type}`,
        item_variant: `${product.golfName}`,
        item_category: 'Activities',
        item_category2: product.type,
        my_booking_golf: {
            golfCode: product.golfCode,
            golfName: product.golfName,
            destination: address,
            fromDate: product.fromDate,
            toDate: product.toDate,
            adults,
            children,
            tickets: mapTickets(product.tickets),
        },
    };
};

const mapTransferProductItem = (product: TransferProduct, quantity = 1): Item => {
    return {
        quantity,
        item_id: product.productId.toString(),
        item_name: `${product.type}`,
        item_category: 'Transport',
        item_category2: product.type,
        item_category3: product.journeyType,
        my_booking_transfer: {
            fromDate: product.from.dateTime,
            toDate: product.to.dateTime,
            from: product.from.address,
            to: product.to.address,
            flightNo: product.flightNo,
            time: product.pickUpTime,
        },
    };
};

const mapAddonProductItem = (product: AnyBookingAddonProduct, quantity = 1): Item => {
    return {
        quantity,
        item_id: product.type,
        item_name: product.name,
        item_category: 'Addon',
        item_category2: product.type,
    };
};

const mapCustomBaggageProductItem = (product: CustomBaggageProduct, quantity = 1): Item => {
    return {
        quantity,
        item_id: `${product.type}`,
        item_name: `${product.description}`,
        item_category: 'Transport',
        item_category2: 'Flight',
        item_category3: product.type,
    };
};
